import {
  CalendarCheck,
  CalendarClock,
  CalendarCog,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  FileSpreadsheet,
  HandCoins,
  House,
  Inbox,
  Landmark,
  LineChart,
  ListChecks,
  ListTodo,
  ListTree,
  MessageSquareReply,
  MessagesSquare,
  Network,
  ReceiptText,
  Scale,
  Smile,
  Target,
  Trophy,
  Users,
  Waves,
} from "lucide-react";
import { ROUTES } from "@/app/routes";
import { Permission } from "@/shared/auth/permissions";
import type { NavItem, NavSection } from "@/shared/components/layout/sidebar-nav.types";

const surveys: NavItem = {
  to: "",
  label: "Surveys",
  icon: Waves,
  moduleId: "surveys",
  children: [
    {
      to: ROUTES.workspace.myPulse.path,
      label: "My surveys",
      icon: Smile,
      requires: Permission.PULSE_SURVEY_RESPOND,
      requiresFeature: "pulse_surveys",
    },
    {
      to: ROUTES.workspace.myActions.path,
      label: "My actions",
      icon: ListChecks,
      requires: Permission.PULSE_ACTION_VIEW_OWN,
    },
    {
      to: ROUTES.workspace.insights.path,
      label: "Insights",
      icon: LineChart,
      requiresAnyOf: [
        Permission.PULSE_DASHBOARD_VIEW_WORKSPACE,
        Permission.PULSE_DASHBOARD_VIEW_DEPARTMENT,
      ],
    },
    {
      to: ROUTES.workspace.pulseSurveys.path,
      label: "Surveys",
      icon: ClipboardList,
      requiresAnyOf: [Permission.PULSE_SURVEY_LIST_WORKSPACE, Permission.PULSE_SURVEY_CREATE],
      requiresFeature: "pulse_surveys",
    },
    {
      to: ROUTES.workspace.pulseAnswerThreads.path,
      label: "Conversations",
      icon: MessagesSquare,
      requires: Permission.PULSE_ANSWER_THREAD_VIEW,
      requiresFeature: "pulse_surveys",
    },
  ],
};

const attendance: NavItem = {
  to: "",
  label: "Attendance",
  icon: CalendarCheck,
  moduleId: "attendance",
  children: [
    {
      to: ROUTES.workspace.attendance.path,
      label: "My attendance",
      icon: CalendarCheck,
      requires: Permission.ATTENDANCE_VIEW_OWN,
      requiresFeature: "hr_core",
    },
    {
      to: ROUTES.workspace.attendanceTeam.path,
      label: "Team",
      icon: CalendarClock,
      requires: Permission.ATTENDANCE_VIEW_SUBTREE,
      requiresFeature: "hr_core",
    },
    {
      to: ROUTES.workspace.attendanceSetup.path,
      label: "Setup",
      icon: CalendarCog,
      requires: Permission.ATTENDANCE_CONFIGURE,
      requiresFeature: "hr_core",
      neverLanding: true,
    },
  ],
};

const leave: NavItem = {
  to: "",
  label: "Leave",
  icon: CalendarDays,
  moduleId: "leave",
  children: [
    {
      to: ROUTES.workspace.leave.path,
      label: "My leave",
      icon: CalendarDays,
      requires: Permission.LEAVE_VIEW_OWN,
      requiresFeature: "hr_core",
    },
    {
      to: ROUTES.workspace.leaveTeam.path,
      label: "Team",
      icon: CalendarClock,
      requires: Permission.LEAVE_VIEW_SUBTREE,
      requiresFeature: "hr_core",
    },
    {
      to: ROUTES.workspace.leaveBalances.path,
      label: "Balances",
      icon: Scale,
      requires: Permission.LEAVE_VIEW_SUBTREE,
      requiresFeature: "hr_core",
    },
    {
      to: ROUTES.workspace.leaveSetup.path,
      label: "Setup",
      icon: CalendarCog,
      requires: Permission.LEAVE_CONFIGURE,
      requiresFeature: "hr_core",
      neverLanding: true,
    },
  ],
};

const myPay: NavItem = {
  to: "",
  label: "My pay",
  icon: ReceiptText,
  moduleId: "my-pay",
  children: [
    {
      to: ROUTES.workspace.myPayslips.path,
      label: "My payslips",
      icon: ReceiptText,
      requires: Permission.SALARY_SLIP_VIEW_OWN,
      requiresFeature: "payroll",
    },
    {
      to: ROUTES.workspace.myPayDetails.path,
      label: "My pay details",
      icon: Landmark,
      requires: Permission.EMPLOYEE_PROFILE_VIEW_OWN,
      requiresFeature: "payroll",
    },
    {
      to: ROUTES.workspace.taxDeclarations.path,
      label: "Tax declarations",
      icon: FileSpreadsheet,
      requires: Permission.TAX_DECLARATION_VIEW_OWN,
      requiresFeature: "payroll",
    },
    {
      to: ROUTES.workspace.benefits.path,
      label: "Benefits",
      icon: HandCoins,
      requires: Permission.BENEFIT_VIEW_OWN,
      requiresFeature: "payroll",
    },
  ],
};

const people: NavItem = {
  to: "",
  label: "People",
  icon: Users,
  moduleId: "people",
  children: [
    {
      to: ROUTES.workspace.people.path,
      label: "Directory",
      icon: Users,
      requires: Permission.WORKSPACE_LIST_MEMBERS,
    },
    { to: ROUTES.workspace.myTeam.path, label: "My team", icon: Network },
    { to: ROUTES.workspace.orgChart.path, label: "Org chart", icon: ListTree },
  ],
};

export const EVERYDAY_WEEK: NavSection = {
  title: "",
  items: [
    { to: ROUTES.workspace.home.path, label: "Home", icon: House },
    {
      to: ROUTES.workspace.approvals.path,
      label: "Approvals",
      icon: Inbox,
      requires: Permission.APPROVAL_VIEW_OWN,
      requiresFeature: "hr_core",
    },
    surveys,
    attendance,
    leave,
    myPay,
    people,
  ],
};

export const EVERYDAY_LATER: NavSection = {
  title: "",
  divider: true,
  items: [
    {
      to: ROUTES.workspace.feedback.path,
      label: "Feedback",
      icon: MessageSquareReply,
      requires: Permission.FEEDBACK_VIEW_OWN,
      requiresFeature: "feedback",
    },
    {
      to: ROUTES.workspace.oneOnOnes.path,
      label: "1:1s",
      icon: MessagesSquare,
      requires: Permission.ONE_ON_ONE_VIEW_OWN,
      requiresFeature: "one_on_ones",
    },
    {
      to: ROUTES.workspace.goals.path,
      label: "Goals",
      icon: Target,
      requires: Permission.GOAL_VIEW_OWN,
      requiresFeature: "goals",
    },
    {
      to: ROUTES.workspace.reviews.path,
      label: "Reviews",
      icon: ClipboardCheck,
      requires: Permission.REVIEW_CYCLE_LIST_MINE,
      requiresFeature: "performance_reviews",
    },
    {
      to: ROUTES.workspace.kudos.path,
      label: "Kudos",
      icon: Trophy,
      requires: Permission.KUDOS_VIEW,
      requiresFeature: "kudos",
    },
    {
      to: ROUTES.workspace.todos.path,
      label: "Todos",
      icon: ListTodo,
      requires: Permission.TODO_VIEW_OWN,
      requiresFeature: "todos",
    },
  ],
};
